import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import {
  Mail, Phone, ChevronLeft, Save, Plus, Trash2, MessageSquare, ShoppingCart,
  Dog as DogIcon, BookOpen, GraduationCap, Receipt, AlertTriangle, PhoneCall,
  FileText, AlertOctagon, Camera, Upload, Link as LinkIcon, Image as ImageIcon,
  Video, FileType2, Megaphone, Archive, ArchiveRestore, Play, ChevronDown, ChevronUp,
  X as XIcon, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';

const NOTE_TONES = {
  general: { bg: '#1B3A87', icon: FileText, label: 'Note' },
  broadcast: { bg: '#C8102E', icon: Megaphone, label: 'Broadcast' },
  call: { bg: '#167A5A', icon: PhoneCall, label: 'Call' },
  visit: { bg: '#B08D57', icon: MessageSquare, label: 'Visit' },
  billing: { bg: '#A50D26', icon: Receipt, label: 'Billing' },
  warning: { bg: '#B45309', icon: AlertOctagon, label: 'Warning' },
};

const STATUS_TONE = {
  PAID: 'bg-[#167A5A]/15 text-[#167A5A] dark:text-[#7DD3A8]',
  PENDING_PAYMENT: 'bg-[#B45309]/15 text-[#B45309] dark:text-[#FBBF24]',
  CHECKED_IN: 'bg-[#1B3A87]/15 text-[#1B3A87] dark:text-[#93C5FD]',
  CHECKED_OUT: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  HOLD: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  CANCELLED: 'bg-[#B42318]/15 text-[#B42318] dark:text-[#FCA5A5]',
  SCHEDULED: 'bg-[#1B3A87]/15 text-[#1B3A87] dark:text-[#93C5FD]',
  COMPLETED: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  IN_PROGRESS: 'bg-[#1B3A87]/15 text-[#1B3A87] dark:text-[#93C5FD]',
};

/**
 * ClientDetail — admin / staff deep-dive on a single client.
 *
 * Supports:
 *  • Avatar upload (admin uploads a profile pic for the client)
 *  • Edit profile fields (name/phone/address/emergency contact)
 *  • Soft-delete (archive) & restore
 *  • Media-rich timeline: text + image / video / PDF / link, optionally
 *    tagged to a specific dog. Only the latest 2 entries are shown on the
 *    profile; "Show all" expands the rest.
 *  • Dog roster with inline photo upload per dog.
 */
export default function ClientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [notes, setNotes] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  // Timeline composer
  const [composer, setComposer] = useState({ body: '', kind: 'broadcast', dog_id: '', link_url: '' });
  const [composerFile, setComposerFile] = useState(null); // { url, type, name }
  const [uploadingComposer, setUploadingComposer] = useState(false);
  const [posting, setPosting] = useState(false);
  const composerFileRef = useRef(null);
  // Timeline collapsing
  const [showAllTimeline, setShowAllTimeline] = useState(false);
  // Avatar upload
  const avatarRef = useRef(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  // Dog photo upload refs
  const dogPhotoRefs = useRef({});
  const [uploadingDogPhoto, setUploadingDogPhoto] = useState(null); // dog_id while uploading

  const canEdit = user && ['ADMIN', 'FRONT_DESK'].includes(user.role);
  const canArchive = user && user.role === 'ADMIN';

  const load = async () => {
    const { data } = await api.get(`/admin/clients/${id}`);
    setDetail(data);
    setForm(data.client);
    const n = await api.get(`/admin/clients/${id}/notes`);
    setNotes(n.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const patch = {
        full_name: form.full_name, phone: form.phone,
        address_line1: form.address_line1, address_line2: form.address_line2,
        city: form.city, state: form.state, postal_code: form.postal_code,
        emergency_contact_name: form.emergency_contact_name,
        emergency_contact_phone: form.emergency_contact_phone,
        emergency_contact_relationship: form.emergency_contact_relationship,
      };
      await api.patch(`/admin/clients/${id}`, patch);
      toast.success('Client profile saved');
      setEditing(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSavingProfile(false); }
  };

  /* ---------- Avatar upload ---------- */
  const onAvatarPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('purpose', 'avatar');
      const { data: uploaded } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await api.post(`/admin/clients/${id}/avatar`, { avatar_url: uploaded.url });
      toast.success('Profile photo updated');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Avatar upload failed'); }
    finally { setUploadingAvatar(false); if (avatarRef.current) avatarRef.current.value = ''; }
  };

  /* ---------- Soft delete / restore ---------- */
  const archiveClient = async () => {
    try {
      await api.delete(`/admin/clients/${id}`);
      toast.success('Client archived');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Archive failed'); }
  };

  const restoreClient = async () => {
    try {
      await api.post(`/admin/clients/${id}/restore`);
      toast.success('Client restored');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Restore failed'); }
  };

  /* ---------- Timeline composer ---------- */
  const detectMediaType = (contentType) => {
    if (!contentType) return 'file';
    if (contentType.startsWith('image/')) return 'image';
    if (contentType.startsWith('video/')) return 'video';
    if (contentType === 'application/pdf') return 'pdf';
    return 'file';
  };

  const onComposerFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingComposer(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('purpose', 'client_media');
      const { data: uploaded } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setComposerFile({
        url: uploaded.url,
        type: detectMediaType(uploaded.content_type),
        name: uploaded.original_filename || file.name,
      });
      toast.success('Attachment ready — tap "Post" to publish');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingComposer(false); if (composerFileRef.current) composerFileRef.current.value = ''; }
  };

  const postTimeline = async (e) => {
    e?.preventDefault?.();
    if (!composer.body.trim() && !composerFile && !composer.link_url.trim()) {
      toast.error('Add a message, attachment, or link to post'); return;
    }
    setPosting(true);
    try {
      await api.post(`/admin/clients/${id}/notes`, {
        body: composer.body,
        kind: composer.kind,
        dog_id: composer.dog_id || null,
        link_url: composer.link_url || null,
        media_url: composerFile?.url || null,
        media_type: composerFile?.type || null,
        media_name: composerFile?.name || null,
      });
      setComposer({ body: '', kind: 'broadcast', dog_id: '', link_url: '' });
      setComposerFile(null);
      const n = await api.get(`/admin/clients/${id}/notes`);
      setNotes(n.data);
      toast.success('Posted');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to post'); }
    finally { setPosting(false); }
  };

  const removeNote = async (nid) => {
    if (!window.confirm('Delete this timeline entry?')) return;
    try {
      await api.delete(`/admin/clients/${id}/notes/${nid}`);
      setNotes(notes.filter(n => n.id !== nid));
    } catch (err) { toast.error('Delete failed'); }
  };

  /* ---------- Dog photo upload ---------- */
  const onDogPhotoPick = async (dogId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDogPhoto(dogId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('purpose', 'dog_photo');
      const { data: uploaded } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await api.post(`/admin/dogs/${dogId}/photo`, { avatar_url: uploaded.url });
      toast.success('Dog photo updated');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingDogPhoto(null); }
  };

  const chargeInPOS = () => navigate(`/admin/pos?client_id=${id}`);

  if (!detail) return <div className='p-10 text-slate-500'>Loading…</div>;
  const c = detail.client;
  const totalSpent = [
    ...detail.bookings.filter(b => b.status === 'PAID' || b.status === 'CHECKED_OUT'),
    ...detail.training_bookings.filter(b => b.status === 'PAID' || b.status === 'COMPLETED'),
  ].reduce((s, b) => s + (b.amount || 0), 0) + detail.pos_orders.filter(o => o.payment_status === 'paid').reduce((s, o) => s + (o.total || 0), 0);

  const visibleNotes = showAllTimeline ? notes : notes.slice(0, 2);
  const hiddenCount = Math.max(0, notes.length - 2);

  return (
    <div className='p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto'>
      <div className='flex items-center gap-3 mb-4'>
        <Button variant='ghost' size='sm' className='text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800' onClick={() => navigate('/admin/clients')} data-testid='back-to-clients'>
          <ChevronLeft className='w-4 h-4 mr-1' /> All Clients
        </Button>
        {c.archived_at && (
          <Badge className='bg-slate-500 text-white' data-testid='archived-badge'>
            Archived — {new Date(c.archived_at).toLocaleDateString()}
          </Badge>
        )}
      </div>

      <div className='flex flex-wrap items-start justify-between gap-4 mb-6'>
        <div className='flex items-center gap-4'>
          <div className='relative'>
            {c.avatar_url ? (
              <img
                src={c.avatar_url}
                alt={c.full_name}
                className='w-16 h-16 rounded-full object-cover shadow-lg border-2 border-white dark:border-slate-800'
                data-testid='client-avatar-img'
              />
            ) : (
              <div className='w-16 h-16 rounded-full bg-[#1B3A87] text-white flex items-center justify-center font-display font-bold text-2xl shadow-lg' data-testid='client-avatar-fallback'>
                {c.full_name?.[0] || '?'}
              </div>
            )}
            {canEdit && (
              <>
                <button
                  type='button'
                  onClick={() => avatarRef.current?.click()}
                  className='absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#C8102E] hover:bg-[#A50D26] text-white flex items-center justify-center shadow-md border-2 border-white dark:border-slate-800 disabled:opacity-50'
                  disabled={uploadingAvatar}
                  aria-label='Upload profile photo'
                  data-testid='upload-client-avatar-btn'
                >
                  <Camera className='w-3.5 h-3.5' />
                </button>
                <input
                  ref={avatarRef}
                  type='file'
                  accept='image/*'
                  className='hidden'
                  onChange={onAvatarPick}
                  data-testid='upload-client-avatar-input'
                />
              </>
            )}
          </div>
          <div>
            <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Client Profile</div>
            <h1 className='font-display text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-50 mt-1'>{c.full_name}</h1>
            <div className='text-xs text-slate-500 dark:text-slate-400 flex items-center gap-3 mt-1 flex-wrap'>
              <span className='flex items-center gap-1'><Mail className='w-3 h-3' /> {c.email}</span>
              {c.phone && (
                <a href={`tel:${c.phone}`} className='flex items-center gap-1 hover:text-[#C8102E]'><Phone className='w-3 h-3' /> {c.phone}</a>
              )}
              {c.membership_level && <Badge className='bg-[#C8102E] text-white border-0'>{c.membership_level} Member</Badge>}
            </div>
          </div>
        </div>
        <div className='flex gap-2 flex-wrap'>
          {canEdit && (
            <Button
              variant='outline'
              className='border-slate-200 text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800'
              onClick={() => setEditing(!editing)}
              data-testid='edit-profile-button'
            >
              {editing ? 'Cancel' : 'Edit Profile'}
            </Button>
          )}
          <Button
            className='bg-[#1B3A87] hover:bg-[#16306F] text-white font-semibold shadow-md'
            onClick={chargeInPOS}
            data-testid='charge-in-pos-button'
          >
            <ShoppingCart className='w-4 h-4 mr-2' /> Charge in POS
          </Button>
          {canArchive && !c.archived_at && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant='outline' className='border-[#B45309] text-[#B45309] hover:bg-[#B45309]/5' data-testid='archive-client-btn'>
                  <Archive className='w-4 h-4 mr-2' /> Archive
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid='archive-confirm-dialog'>
                <AlertDialogHeader>
                  <AlertDialogTitle>Archive {c.full_name}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    The client will be hidden from the main directory. Their dogs,
                    bookings and timeline entries stay intact and can be restored
                    at any time.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid='archive-cancel'>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={archiveClient}
                    className='bg-[#B45309] hover:bg-[#92400E]'
                    data-testid='archive-confirm'
                  >
                    Archive
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {canArchive && c.archived_at && (
            <Button
              variant='outline'
              onClick={restoreClient}
              className='border-emerald-600 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:border-emerald-700/40 dark:hover:bg-emerald-900/20'
              data-testid='restore-client-btn'
            >
              <ArchiveRestore className='w-4 h-4 mr-2' /> Restore
            </Button>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className='grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5'>
        {[
          {l:'Dogs',v:detail.dogs.length, icon:DogIcon, color:'#1B3A87'},
          {l:'Boardings',v:detail.bookings.length, icon:BookOpen, color:'#167A5A'},
          {l:'Training',v:detail.training_bookings.length, icon:GraduationCap, color:'#C8102E'},
          {l:'Total Spent',v:`$${totalSpent.toFixed(0)}`, icon:Receipt, color:'#B08D57'},
        ].map(k => (
          <Card key={k.l} className='p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'>
            <div className='flex items-center justify-between'>
              <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 dark:text-slate-400'>{k.l}</div>
              <k.icon className='w-4 h-4' style={{color: k.color}} />
            </div>
            <div className='font-display text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-50 mt-1'>{k.v}</div>
          </Card>
        ))}
      </div>

      <div className='grid lg:grid-cols-3 gap-5'>
        {/* Left: Profile + Timeline */}
        <div className='lg:col-span-2 space-y-5'>
          {/* Profile card */}
          <Card className='p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'>
            <h3 className='font-display font-semibold text-lg mb-4 text-slate-900 dark:text-slate-50'>Profile</h3>
            {editing ? (
              <form onSubmit={saveProfile} className='space-y-3 text-sm'>
                <div className='grid sm:grid-cols-2 gap-3'>
                  <div><Label>Full Name</Label><Input value={form.full_name||''} onChange={(e)=>setForm({...form, full_name: e.target.value})} data-testid='cd-name' /></div>
                  <div><Label>Phone</Label><Input value={form.phone||''} onChange={(e)=>setForm({...form, phone: e.target.value})} data-testid='cd-phone' /></div>
                  <div className='sm:col-span-2'><Label>Address Line 1</Label><Input value={form.address_line1||''} onChange={(e)=>setForm({...form, address_line1: e.target.value})} data-testid='cd-addr1' /></div>
                  <div className='sm:col-span-2'><Label>Address Line 2</Label><Input value={form.address_line2||''} onChange={(e)=>setForm({...form, address_line2: e.target.value})} data-testid='cd-addr2' /></div>
                  <div><Label>City</Label><Input value={form.city||''} onChange={(e)=>setForm({...form, city: e.target.value})} data-testid='cd-city' /></div>
                  <div className='grid grid-cols-2 gap-2'>
                    <div><Label>State</Label><Input value={form.state||''} onChange={(e)=>setForm({...form, state: e.target.value})} data-testid='cd-state' /></div>
                    <div><Label>ZIP</Label><Input value={form.postal_code||''} onChange={(e)=>setForm({...form, postal_code: e.target.value})} data-testid='cd-zip' /></div>
                  </div>
                  <div className='sm:col-span-2 border-t border-slate-200 dark:border-slate-700 pt-3'><div className='text-[10px] uppercase tracking-wider font-bold text-[#C8102E] mb-2'>Emergency Contact</div></div>
                  <div><Label>Name</Label><Input value={form.emergency_contact_name||''} onChange={(e)=>setForm({...form, emergency_contact_name: e.target.value})} data-testid='cd-ec-name' /></div>
                  <div><Label>Phone</Label><Input value={form.emergency_contact_phone||''} onChange={(e)=>setForm({...form, emergency_contact_phone: e.target.value})} data-testid='cd-ec-phone' /></div>
                  <div className='sm:col-span-2'><Label>Relationship</Label><Input value={form.emergency_contact_relationship||''} onChange={(e)=>setForm({...form, emergency_contact_relationship: e.target.value})} data-testid='cd-ec-rel' /></div>
                </div>
                <Button type='submit' disabled={savingProfile} className='bg-[#1B3A87] hover:bg-[#16306F] text-white font-semibold' data-testid='cd-save-button'><Save className='w-4 h-4 mr-2' /> {savingProfile ? 'Saving…' : 'Save changes'}</Button>
              </form>
            ) : (
              <div className='grid sm:grid-cols-2 gap-3 text-sm text-slate-600 dark:text-slate-300'>
                <div><span className='text-slate-500 dark:text-slate-500 text-xs uppercase tracking-wider'>Email</span><div>{c.email}</div></div>
                <div><span className='text-slate-500 dark:text-slate-500 text-xs uppercase tracking-wider'>Phone</span><div>{c.phone || '—'}</div></div>
                <div className='sm:col-span-2'><span className='text-slate-500 dark:text-slate-500 text-xs uppercase tracking-wider'>Address</span><div>{[c.address_line1, c.address_line2, c.city, c.state, c.postal_code].filter(Boolean).join(', ') || '—'}</div></div>
                <div className='sm:col-span-2 border-t border-slate-200 dark:border-slate-700 pt-3'><div className='text-[10px] uppercase tracking-wider font-bold text-[#C8102E] mb-2'>Emergency Contact</div></div>
                <div><span className='text-slate-500 dark:text-slate-500 text-xs uppercase tracking-wider'>Name</span><div>{c.emergency_contact_name || '—'}</div></div>
                <div><span className='text-slate-500 dark:text-slate-500 text-xs uppercase tracking-wider'>Phone</span><div>{c.emergency_contact_phone || '—'}</div></div>
                <div className='sm:col-span-2'><span className='text-slate-500 dark:text-slate-500 text-xs uppercase tracking-wider'>Relationship</span><div>{c.emergency_contact_relationship || '—'}</div></div>
              </div>
            )}
          </Card>

          {/* ============ Timeline / Broadcast Composer ============ */}
          {canEdit && (
            <Card className='p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800' data-testid='timeline-composer'>
              <div className='flex items-center gap-2 mb-3'>
                <Megaphone className='w-5 h-5 text-[#C8102E]' />
                <h3 className='font-display font-semibold text-lg text-slate-900 dark:text-slate-50'>
                  Post to timeline
                </h3>
              </div>
              <form onSubmit={postTimeline} className='space-y-3'>
                <Textarea
                  rows={2}
                  placeholder='Share an update, a photo, a video of their dog…'
                  value={composer.body}
                  onChange={(e) => setComposer({ ...composer, body: e.target.value })}
                  data-testid='timeline-body-input'
                />

                {/* Attachment preview */}
                {composerFile && (
                  <div className='relative inline-flex items-center gap-2 p-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700'>
                    {composerFile.type === 'image' && <ImageIcon className='w-4 h-4 text-[#1B3A87]' />}
                    {composerFile.type === 'video' && <Video className='w-4 h-4 text-[#C8102E]' />}
                    {composerFile.type === 'pdf' && <FileType2 className='w-4 h-4 text-[#B45309]' />}
                    <span className='text-xs font-medium text-slate-700 dark:text-slate-300 truncate max-w-[160px]'>{composerFile.name}</span>
                    <button
                      type='button'
                      onClick={() => setComposerFile(null)}
                      className='text-slate-400 hover:text-[#C8102E]'
                      aria-label='Remove attachment'
                      data-testid='remove-attachment-btn'
                    >
                      <XIcon className='w-3.5 h-3.5' />
                    </button>
                  </div>
                )}

                {/* Options row — dog tag + link */}
                <div className='grid sm:grid-cols-2 gap-3'>
                  {detail.dogs.length > 0 && (
                    <div>
                      <Label className='text-xs'>Tag a dog (optional)</Label>
                      <select
                        value={composer.dog_id}
                        onChange={(e) => setComposer({ ...composer, dog_id: e.target.value })}
                        className='flex h-10 w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm'
                        data-testid='timeline-dog-select'
                      >
                        <option value=''>— No dog —</option>
                        {detail.dogs.map(d => (
                          <option key={d.id} value={d.id}>{d.name}{d.breed ? ` (${d.breed})` : ''}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div>
                    <Label className='text-xs'>Add a link (optional)</Label>
                    <Input
                      placeholder='https://…'
                      value={composer.link_url}
                      onChange={(e) => setComposer({ ...composer, link_url: e.target.value })}
                      data-testid='timeline-link-input'
                    />
                  </div>
                </div>

                {/* Toolbar row */}
                <div className='flex items-center justify-between flex-wrap gap-2'>
                  <div className='flex items-center gap-2 flex-wrap'>
                    <select
                      value={composer.kind}
                      onChange={(e) => setComposer({ ...composer, kind: e.target.value })}
                      className='h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm px-2'
                      data-testid='timeline-kind-select'
                    >
                      {Object.entries(NOTE_TONES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      className='border-slate-300 dark:border-slate-600'
                      onClick={() => composerFileRef.current?.click()}
                      disabled={uploadingComposer}
                      data-testid='timeline-attach-btn'
                    >
                      {uploadingComposer ? (
                        <><RotateCcw className='w-4 h-4 mr-1.5 animate-spin' /> Uploading…</>
                      ) : (
                        <><Upload className='w-4 h-4 mr-1.5' /> Attach</>
                      )}
                    </Button>
                    <input
                      ref={composerFileRef}
                      type='file'
                      accept='image/*,video/*,application/pdf'
                      className='hidden'
                      onChange={onComposerFilePick}
                      data-testid='timeline-attach-input'
                    />
                  </div>
                  <Button
                    type='submit'
                    disabled={posting}
                    className='bg-[#C8102E] hover:bg-[#A50D26] text-white font-semibold shadow-md'
                    data-testid='timeline-post-btn'
                  >
                    {posting ? 'Posting…' : 'Post'}
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {/* ============ Timeline Feed ============ */}
          <Card className='p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'>
            <div className='flex items-center justify-between mb-3 flex-wrap gap-2'>
              <h3 className='font-display font-semibold text-lg text-slate-900 dark:text-slate-50 flex items-center gap-2'>
                <FileText className='w-5 h-5' /> Timeline
                <Badge variant='secondary' className='bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'>{notes.length}</Badge>
              </h3>
              {notes.length > 2 && (
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => setShowAllTimeline(!showAllTimeline)}
                  className='text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                  data-testid='timeline-toggle-btn'
                >
                  {showAllTimeline ? (
                    <><ChevronUp className='w-4 h-4 mr-1' /> Show less</>
                  ) : (
                    <><ChevronDown className='w-4 h-4 mr-1' /> Show all ({hiddenCount} more)</>
                  )}
                </Button>
              )}
            </div>
            {notes.length === 0 ? (
              <div className='text-sm text-slate-500 dark:text-slate-400 text-center py-6'>
                No posts yet. {canEdit ? 'Use the composer above to send the first update.' : ''}
              </div>
            ) : (
              <div className='space-y-3'>
                {visibleNotes.map(n => <TimelineEntry key={n.id} entry={n} canEdit={canEdit} onDelete={() => removeNote(n.id)} />)}
              </div>
            )}
          </Card>
        </div>

        {/* Right: Dogs + History */}
        <div className='space-y-5'>
          <Card className='p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'>
            <h3 className='font-display font-semibold text-lg text-slate-900 dark:text-slate-50 mb-3 flex items-center gap-2'><DogIcon className='w-5 h-5' /> Dogs ({detail.dogs.length})</h3>
            {detail.dogs.length === 0 ? <div className='text-sm text-slate-500 dark:text-slate-400'>No dogs registered.</div> : (
              <div className='space-y-2'>
                {detail.dogs.map(d => {
                  const photo = d.photo_url || d.photo_base64;
                  const uploading = uploadingDogPhoto === d.id;
                  return (
                    <div key={d.id} className='flex items-center gap-3 p-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700' data-testid={`cd-dog-${d.id}`}>
                      <div className='relative shrink-0'>
                        {photo ? (
                          <img src={photo} alt={d.name} className='w-12 h-12 rounded-full object-cover' />
                        ) : (
                          <div className='w-12 h-12 rounded-full bg-[#1B3A87] text-white flex items-center justify-center font-bold'>{d.name?.[0] || '?'}</div>
                        )}
                        {canEdit && (
                          <>
                            <button
                              type='button'
                              onClick={() => dogPhotoRefs.current[d.id]?.click()}
                              className='absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#1B3A87] hover:bg-[#16306F] text-white flex items-center justify-center shadow border border-white dark:border-slate-800 disabled:opacity-50'
                              disabled={uploading}
                              aria-label='Upload dog photo'
                              data-testid={`upload-dog-photo-${d.id}`}
                            >
                              <Camera className='w-2.5 h-2.5' />
                            </button>
                            <input
                              ref={(el) => (dogPhotoRefs.current[d.id] = el)}
                              type='file'
                              accept='image/*'
                              className='hidden'
                              onChange={(e) => onDogPhotoPick(d.id, e)}
                            />
                          </>
                        )}
                      </div>
                      <div className='min-w-0 flex-1'>
                        <div className='font-semibold text-slate-900 dark:text-slate-50 text-sm truncate'>{d.name}</div>
                        <div className='text-[10px] text-slate-500 dark:text-slate-400 truncate'>{d.breed || '—'}{d.assigned_trainer_name ? ` · ${d.assigned_trainer_name}` : ''}</div>
                      </div>
                      {!d.vax_compliant && <AlertTriangle className='w-4 h-4 text-[#B45309]' aria-label='VAX needed' />}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          <Card className='p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'>
            <Tabs defaultValue='boardings'>
              <TabsList className='mb-3 flex-wrap'>
                <TabsTrigger value='boardings' data-testid='cd-tab-boardings'>Boardings ({detail.bookings.length})</TabsTrigger>
                <TabsTrigger value='training' data-testid='cd-tab-training'>Training ({detail.training_bookings.length})</TabsTrigger>
                <TabsTrigger value='orders' data-testid='cd-tab-orders'>POS ({detail.pos_orders.length})</TabsTrigger>
              </TabsList>
              <TabsContent value='boardings'>
                {detail.bookings.length === 0 ? <div className='text-sm text-slate-500 dark:text-slate-400'>No boardings.</div> : (
                  <div className='space-y-2'>{detail.bookings.slice(0,8).map(b => (
                    <div key={b.id} className='p-2 rounded bg-slate-50 dark:bg-slate-800 text-xs'><div className='flex items-center justify-between'><span className='font-semibold text-slate-900 dark:text-slate-50'>{b.dog_name} · {b.run_name}</span><Badge className={STATUS_TONE[b.status] || ''}>{b.status.replace('_',' ')}</Badge></div><div className='text-slate-500 dark:text-slate-400 mt-0.5'>{new Date(b.start).toLocaleDateString()} → {new Date(b.end).toLocaleDateString()} · ${b.amount.toFixed(2)}</div></div>
                  ))}</div>
                )}
              </TabsContent>
              <TabsContent value='training'>
                {detail.training_bookings.length === 0 ? <div className='text-sm text-slate-500 dark:text-slate-400'>No training bookings.</div> : (
                  <div className='space-y-2'>{detail.training_bookings.slice(0,8).map(b => (
                    <div key={b.id} className='p-2 rounded bg-slate-50 dark:bg-slate-800 text-xs'><div className='flex items-center justify-between'><span className='font-semibold text-slate-900 dark:text-slate-50'>{b.dog_name} · {b.program_label}</span><Badge className={STATUS_TONE[b.status] || ''}>{b.status.replace('_',' ')}</Badge></div><div className='text-slate-500 dark:text-slate-400 mt-0.5'>{b.trainer_name} · {new Date(b.start).toLocaleDateString()} → {new Date(b.end).toLocaleDateString()} · ${b.amount.toFixed(2)}</div></div>
                  ))}</div>
                )}
              </TabsContent>
              <TabsContent value='orders'>
                {detail.pos_orders.length === 0 ? <div className='text-sm text-slate-500 dark:text-slate-400'>No POS orders yet.</div> : (
                  <div className='space-y-2'>{detail.pos_orders.slice(0,8).map(o => (
                    <div key={o.id} className='p-2 rounded bg-slate-50 dark:bg-slate-800 text-xs'><div className='flex items-center justify-between'><span className='font-semibold text-slate-900 dark:text-slate-50'>${o.total.toFixed(2)} · {o.items.length} items</span><Badge className={o.payment_status === 'paid' ? 'bg-[#167A5A]/15 text-[#167A5A]' : 'bg-[#B45309]/15 text-[#B45309]'}>{o.payment_status}</Badge></div><div className='text-slate-500 dark:text-slate-400 mt-0.5'>{new Date(o.created_at).toLocaleString()} · {o.payment_method}</div></div>
                  ))}</div>
                )}
              </TabsContent>
            </Tabs>
          </Card>
        </div>
      </div>
    </div>
  );
}

/**
 * TimelineEntry — a single timeline post. Renders text + optional media
 * (image / video player / PDF link) + optional external link + optional
 * dog tag chip. Shared between the collapsed-and-expanded views.
 */
const TimelineEntry = ({ entry: n, canEdit, onDelete }) => {
  const tone = NOTE_TONES[n.kind] || NOTE_TONES.general;
  const Icon = tone.icon;
  return (
    <div className='flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700' data-testid={`timeline-entry-${n.id}`}>
      <div className='w-9 h-9 rounded-lg flex items-center justify-center shrink-0 shadow' style={{ background: tone.bg }}>
        <Icon className='w-4 h-4 text-white' />
      </div>
      <div className='flex-1 min-w-0'>
        <div className='flex items-center gap-2 flex-wrap mb-1'>
          <span className='text-[10px] uppercase tracking-wider font-bold text-slate-600 dark:text-slate-300'>{tone.label}</span>
          {n.dog_name && (
            <Badge className='bg-[#1B3A87]/10 text-[#1B3A87] dark:text-[#93C5FD] text-[10px] gap-1'>
              <DogIcon className='w-2.5 h-2.5' /> {n.dog_name}
            </Badge>
          )}
        </div>
        {n.body && <div className='text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap mb-2'>{n.body}</div>}

        {/* Media rendering */}
        {n.media_url && n.media_type === 'image' && (
          <a href={n.media_url} target='_blank' rel='noopener noreferrer'>
            <img src={n.media_url} alt={n.media_name || 'attachment'} className='rounded-lg max-h-72 object-cover w-full' data-testid={`timeline-media-image-${n.id}`} />
          </a>
        )}
        {n.media_url && n.media_type === 'video' && (
          <video controls className='rounded-lg max-h-72 w-full bg-black' data-testid={`timeline-media-video-${n.id}`}>
            <source src={n.media_url} />
          </video>
        )}
        {n.media_url && n.media_type === 'pdf' && (
          <a
            href={n.media_url}
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm text-slate-700 dark:text-slate-200 hover:border-[#B45309]'
            data-testid={`timeline-media-pdf-${n.id}`}
          >
            <FileType2 className='w-4 h-4 text-[#B45309]' /> {n.media_name || 'View PDF'}
          </a>
        )}
        {n.media_url && n.media_type === 'file' && (
          <a
            href={n.media_url}
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm'
          >
            <FileText className='w-4 h-4' /> {n.media_name || 'Download'}
          </a>
        )}
        {n.link_url && (
          <a
            href={n.link_url}
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex items-center gap-1 text-xs text-[#1B3A87] dark:text-[#93C5FD] hover:underline mt-2 break-all'
            data-testid={`timeline-link-${n.id}`}
          >
            <LinkIcon className='w-3 h-3' /> {n.link_url}
          </a>
        )}

        <div className='text-[10px] text-slate-500 dark:text-slate-400 mt-2'>
          {n.author_name} ({n.author_role}) · {new Date(n.created_at).toLocaleString()}
        </div>
      </div>
      {canEdit && (
        <button
          className='text-slate-400 hover:text-[#B42318] shrink-0'
          onClick={onDelete}
          aria-label='Delete entry'
          data-testid={`timeline-delete-${n.id}`}
        >
          <Trash2 className='w-3.5 h-3.5' />
        </button>
      )}
    </div>
  );
};
